import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  IconBooks,
  IconBriefcase,
  IconBuildingCommunity,
  IconChartPie,
  IconCloudDollar,
  IconDeviceDesktopOff,
  IconPresentationAnalytics,
  IconReportMoney,
  IconSparkles,
  IconTrendingUp,
} from '@tabler/icons-react';
import React from 'react';
import Header from '../components/Header';
import {
  CardGridSection,
  FinalCTASection,
  FooterNew,
  HeroSectionNew,
  HowItWorks,
  WhyRSMC,
} from '../components/organisms';
import SEO from '../components/SEO';
import { useAnalytics } from '../hooks/useAnalytics';

/**
 * Home page component for EllaFin - AWS and Financial Reporting as a Service
 *
 * Sections:
 * 1. Hero - Value proposition, video, and proof points
 * 2. The Gap We Close - Why finance needs a clear view of cloud spend
 * 3. How It Works - Discover, Build, Deliver
 * 4. One Intelligence Layer - The three solution areas
 * 5. Who It's For - Target organizations
 * 6. Why RSMC - Delivery partner and AWS credentials
 * 7. Final CTA - Full-width call-to-action
 * 8. Footer - Multi-column footer
 *
 * @returns {React.ReactElement} The home page
 */
function HomePageNew() {
  const theme = useTheme();
  const { trackCTA } = useAnalytics();

  /**
   * Handle primary CTA click
   * @param {string} location - Where the CTA was clicked
   */
  const handlePrimaryCTA = (location) => {
    trackCTA('Schedule a Discovery Call', location, {
      destination: '/early-access#cta',
    });
  };

  const iconProps = { size: 28, stroke: 1.5, color: '#5e7b08' };

  return (
    <Box
      sx={{
        textAlign: 'left',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <SEO
        title="AWS and Financial Reporting as a Service"
        description="EllaFin gives Leadership, Finance, and Procurement teams executive-ready AWS billing, dashboards, and financial reporting, defined by you, maintained by RSMC."
        path="/"
        keywords="AWS financial reporting, AWS billing reports, executive dashboards, business intelligence as a service, Amazon Quick Sight, cloud spend reporting"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'EllaFin',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description:
            'AWS and financial reporting as a service. Executive-ready AWS billing, dashboards, and financial reporting, defined by you, maintained by RSMC.',
          url: 'https://www.ellafin.ai',
          provider: {
            '@type': 'Organization',
            name: 'Ready Secure Managed Cloud',
            url: 'https://www.ellafin.ai',
          },
        }}
      />

      {/* Navigation Header */}
      <Header />

      {/* Section 1: Hero */}
      <HeroSectionNew onPrimaryCtaClick={() => handlePrimaryCTA('Hero')} />

      {/* Section 2: The Gap We Close */}
      <CardGridSection
        sectionId="the-gap"
        eyebrow="The Gap We Close"
        headline="Give finance a clear view of every cloud dollar."
        subheadline="AWS billing lives in the console, in formats built for engineers. Finance works in spreadsheets and the tools they already trust. EllaFin connects the two and delivers the result for you."
        items={[
          {
            title: 'Reporting built for finance',
            description:
              'Cloud costs presented in business terms, aligned to your P&L and budget, ready to read at a glance.',
            icon: <IconReportMoney {...iconProps} />,
          },
          {
            title: 'No console, no new platform',
            description:
              'Your team never logs into AWS and never learns another SaaS tool. The reporting is delivered in EllaFin, powered by Amazon Quick Sight.',
            icon: <IconDeviceDesktopOff {...iconProps} />,
          },
          {
            title: 'A view made for your board',
            description:
              'Give board members, investors, and advisors a personalized view of your financial health, without handing over access to your complete financial data or accounting software.',
            icon: <IconPresentationAnalytics {...iconProps} />,
          },
        ]}
      />

      {/* Section 3: How It Works */}
      <HowItWorks sectionId="how-it-works" />

      {/* Section 4: One Intelligence Layer */}
      <CardGridSection
        sectionId="solution"
        headline="One Intelligence Layer"
        subheadline="Everything a finance leader needs to see cloud spend as a business story."
        backgroundColor={theme.palette.background.section}
        items={[
          {
            title: 'Executive Financial Reporting',
            description:
              'Already reporting in spreadsheets or another BI tool? We bring that data into the same clear, visual experience alongside your AWS spend, so leadership sees one picture in one place.',
            icon: <IconChartPie {...iconProps} />,
          },
          {
            title: 'AI-Driven Business Intelligence',
            description:
              'Ask questions of your financial data using governed AI agents grounded in your own data.',
            icon: <IconSparkles {...iconProps} />,
          },
          {
            title: 'Cloud Financial Management',
            description:
              'Your FinOps Advisors build your AWS spend reporting in your financial language, mapping spend to your programs, departments, and budget lines, with monthly billing reports and clear savings insights.',
            icon: <IconCloudDollar {...iconProps} />,
          },
        ]}
      />

      {/* Section 5: Who It's For */}
      <CardGridSection
        sectionId="who-its-for"
        eyebrow="Who It's For"
        headline="An enterprise business intelligence toolset, without the learning curve."
        subheadline="You get the full power of an enterprise toolset. You just never have to learn it."
        columns={4}
        items={[
          {
            title: 'SMBs',
            icon: <IconBriefcase {...iconProps} />,
          },
          {
            title: 'Startups',
            icon: <IconTrendingUp {...iconProps} />,
          },
          {
            title: 'Nonprofits',
            icon: <IconBuildingCommunity {...iconProps} />,
          },
          {
            title: 'Education',
            icon: <IconBooks {...iconProps} />,
          },
        ]}
      />

      {/* Section 6: Why RSMC */}
      <WhyRSMC sectionId="why-rsmc" />

      {/* Section 7: Final CTA */}
      <FinalCTASection
        sectionId="final-cta"
        headline="Ready to see your AWS spend in business terms?"
        subheadline="Let us show you what executive-ready cloud and financial reporting looks like, delivered as a service."
        primaryCtaText="Schedule a Discovery Call"
        primaryCtaHref="/early-access#cta"
        secondaryCtaText={null}
        onPrimaryCtaClick={() => handlePrimaryCTA('FinalCTA')}
      />

      {/* Section 8: Footer */}
      <FooterNew />
    </Box>
  );
}

export default HomePageNew;
